import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

import { AuthContext } from '../components/AuthProvider';

import {
	get_bearer,
	update_bearer,
	delete_bearer
} from "../tools-network";

import {
	validate_name
} from "../tools";

import { showTemporaryMessage } from "../components/Message";
import ScanTokens from "../components/ScanTokens";
import MultiDiseaseSelector from "../components/MultiDiseaseSelector";
import BearerMedicalDisplay from '../../../../shared/components/BearerMedicalDisplay';
import BearerAuditLog from "../components/BearerAuditLog";
import BearerAccessLog from "../components/BearerAccessLog";

const BearerDetails = ({loginToken, bearerId, refreshListOfBearers, reportEditingState}) => {
	const { t } = useTranslation();

	const { userType } = React.useContext(AuthContext);

	const [bearerDetails, setBearerDetails] = useState(null);

	const updateBearerDetails = async () => {
		if(bearerId == 0) {
			return;
		}
		try {
			const res = await get_bearer(loginToken, bearerId);
			if(res.status) {
				const bearerDetails = res.data;
				setBearerDetails(bearerDetails);
			} else {
				showTemporaryMessage("Fetch bearer details FAILED!", res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage("Fetch bearer details FAILED!", err, 'danger');
		}
	};

	useEffect(() => {
		updateBearerDetails();
	}, [bearerId]);

	const [submitGuiEnabled, setSubmitGuiEnabled]				= useState(true);
	const [isEditingWristband, setIsEditingWristband]				= useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm]				= useState(false);
	const [deleteConfirmName, setDeleteConfirmName]				= useState('');
	const [isSaving, setIsSaving]								= useState(false);
	
	// Overview edit mode - when true, fields are directly editable
	const [isInOverviewEditMode, setIsInOverviewEditMode]		= useState(false);
	
	// Health conditions editing mode
	const [isEditingHealthConditions, setIsEditingHealthConditions] = useState(false);
	
	// Field values
	const [name, setName]										= useState('');
	const [birthYear, setBirthYear]								= useState('');
	const [sex, setSex]											= useState('');
	const [bloodType, setBloodType]								= useState('');
	const [isEnabled, setIsEnabled]								= useState(false);
	const [isVerbal, setIsVerbal]								= useState(true);
	const [diseases, setDiseases]								= useState([]);

	useEffect(() => {
		// Update bearer's diseases
		if (bearerDetails && bearerDetails.diseases) {
			setDiseases(bearerDetails.diseases);
		} else {
			setDiseases([]);
		}

		// Update name
		if (bearerDetails && bearerDetails.name !== undefined) {
			setName(bearerDetails.name);
		} else {
			setName("");
		}

		// Update medical info
		if (bearerDetails) {
			const bYear = (bearerDetails.birth_year !== undefined && bearerDetails.birth_year !== null && bearerDetails.birth_year >= 0) 
				? bearerDetails.birth_year.toString() 
				: '-1';
			setBirthYear(bYear);
			
			const bSex = bearerDetails.sex || 'unspecified';
			setSex(bSex);
			
			const bBlood = bearerDetails.blood_type || 'unspecified';
			setBloodType(bBlood);
		} else {
			setBirthYear('-1');
			setSex('unspecified');
			setBloodType('unspecified');
		}

		// Update enabled status
		setIsEnabled(!!(bearerDetails && bearerDetails.isenabled));

		// Update verbal status
		if (bearerDetails && bearerDetails.is_verbal !== undefined) {
			setIsVerbal(bearerDetails.is_verbal);
		} else {
			setIsVerbal(true);
		}
	}, [bearerDetails]);

	// Report editing state to parent
	useEffect(() => {
		if (reportEditingState) {
			reportEditingState(isInOverviewEditMode || isEditingWristband || isEditingHealthConditions);
		}
	}, [isInOverviewEditMode, isEditingWristband, isEditingHealthConditions]);

	// Individual field save functions
	const saveName = async (newName) => {
		if (!validate_name(newName)) {
			showTemporaryMessage("ERROR", "Name is not valid!", 'danger');
			return;
		}
		
		setIsSaving(true);
		try {
			const res = await update_bearer(loginToken, bearerDetails.id, { name: newName });
			if (res.success) {
				showTemporaryMessage(t("bearer_information_successfully_updated"), "", 'success');
				setName(newName);
				updateBearerDetails();
				refreshListOfBearers(); // Name change affects list
			} else {
				showTemporaryMessage("Update bearer FAILED!", res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage("Update bearer FAILED!", err, 'danger');
		} finally {
			setIsSaving(false);
		}
	};

	const saveBirthYear = async (newBirthYear) => {
		setIsSaving(true);
		try {
			const birth_year = (newBirthYear && newBirthYear !== '-1') ? parseInt(newBirthYear) : null;
			const res = await update_bearer(loginToken, bearerDetails.id, { birth_year });
			if (res.success) {
				showTemporaryMessage(t("bearer_information_successfully_updated"), "", 'success');
				setBirthYear(newBirthYear);
				updateBearerDetails();
			} else {
				showTemporaryMessage("Update bearer FAILED!", res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage("Update bearer FAILED!", err, 'danger');
		} finally {
			setIsSaving(false);
		}
	};

	const saveSex = async (newSex) => {
		setIsSaving(true);
		try {
			const sexValue = (newSex && newSex !== 'unspecified') ? newSex : null;
			const res = await update_bearer(loginToken, bearerDetails.id, { sex: sexValue });
			if (res.success) {
				showTemporaryMessage(t("bearer_information_successfully_updated"), "", 'success');
				setSex(newSex);
				updateBearerDetails();
			} else {
				showTemporaryMessage("Update bearer FAILED!", res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage("Update bearer FAILED!", err, 'danger');
		} finally {
			setIsSaving(false);
		}
	};

	const saveBloodType = async (newBloodType) => {
		setIsSaving(true);
		try {
			const blood_type = (newBloodType && newBloodType !== 'unspecified') ? newBloodType : null;
			const res = await update_bearer(loginToken, bearerDetails.id, { blood_type });
			if (res.success) {
				showTemporaryMessage(t("bearer_information_successfully_updated"), "", 'success');
				setBloodType(newBloodType);
				updateBearerDetails();
			} else {
				showTemporaryMessage("Update bearer FAILED!", res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage("Update bearer FAILED!", err, 'danger');
		} finally {
			setIsSaving(false);
		}
	};

	const saveIsVerbal = async (newValue) => {
		setIsSaving(true);
		try {
			const res = await update_bearer(loginToken, bearerDetails.id, { is_verbal: newValue });
			if (res.success) {
				showTemporaryMessage(t("bearer_information_successfully_updated"), "", 'success');
				setIsVerbal(newValue);
				updateBearerDetails();
			} else {
				showTemporaryMessage("Update bearer FAILED!", res.msg, 'danger');
				// Revert on failure
				setIsVerbal(!newValue);
			}
		} catch (err) {
			showTemporaryMessage("Update bearer FAILED!", err, 'danger');
			setIsVerbal(!newValue);
		} finally {
			setIsSaving(false);
		}
	};

	const saveDiseases = async (diseasesToSave) => {
		setIsSaving(true);
		try {
			const res = await update_bearer(loginToken, bearerDetails.id, { diseases: diseasesToSave });
			if (res.success) {
				showTemporaryMessage(t("bearer_information_successfully_updated"), "", 'success');
				updateBearerDetails();
			} else {
				showTemporaryMessage("Update bearer FAILED!", res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage("Update bearer FAILED!", err, 'danger');
		} finally {
			setIsSaving(false);
		}
	};

	async function confirmDeleteBearer() {
		if (deleteConfirmName !== bearerDetails.name) {
			showTemporaryMessage(t("name_does_not_match"), "", 'danger');
			return;
		}
		setIsSaving(true);
		try {
			const res = await delete_bearer(loginToken, bearerDetails.id);
			if(res.success) {
				showTemporaryMessage(t("bearer_successfully_deleted"));
				setShowDeleteConfirm(false);
				setDeleteConfirmName('');
				refreshListOfBearers();
			} else {
				showTemporaryMessage("Delete bearer FAILED!", res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage("Delete bearer FAILED!", err, 'danger');
		} finally {
			setIsSaving(false);
		}
	}

	const updateListOfScanTokens = async () => {
		console.log("Update list of scan tokens!");
	};

	return(bearerId == 0 ?
		<div className="flex flex-row flex-wrap gap-5 w-full align-top justify-between default bearer-details">
			<h3 className="my-2 text-2xl text-center font-bold">{t("select_bearer")}</h3>
		</div>
			:
		<div className="flex flex-row flex-wrap gap-5 w-full align-top justify-between default bearer-details">
			<div className={`flex-wrap content-box gap-2 neumorp bg-white px-8 py-8 w-full md:max-w-lg min-w-0 ${isSaving ? 'opacity-60 pointer-events-none' : ''}`}>
				<div>
					<h3 className="my-2 text-2xl text-center font-bold">{t("information_about")} {bearerDetails?.name || ''}</h3>
				</div>
				
				{/* ID and IS ENABLED (admin only) */}
				<div className="flex w-full items-center gap-2 py-1 justify-between">
					{(userType == 'ADMIN' || userType == 'SUPERUSER') &&
						<div className="flex items-center justify-self-start">
							<span className="font-bold text-nowrap bearer-detail bearer-detail-id">
								{t("id")}: {bearerId}
							</span>
						</div>
					}
					{(userType == 'ADMIN' || userType == 'SUPERUSER') &&
						<div className="flex items-center justify-self-end">
							<label htmlFor="bearer-detail-isenabled" className="bearer-detail bearer-detail-isenabled text-sm font-medium leading-4 text-gray-700 mr-1 text-nowrap">
								{t("enabled")}:
							</label>
							<input
								disabled={isSaving || isEditingWristband || isEditingHealthConditions}
								type="checkbox"
								id="bearer-detail-isenabled"
								checked={isEnabled}
								onChange={(e) => {
									setIsEnabled(e.target.checked);}}
								name="isenabled"
								className="bearer-detail h-5 w-5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"/> 
						</div>
					}
				</div>
				
				{/* PUBLIC PREVIEW */}
				<div className="mt-6 border-2 border-cyan-200 rounded-lg p-4 bg-cyan-50">
					<h4 className="text-sm font-semibold text-cyan-900 mb-1 flex items-center">
						<svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
						</svg>
						{t("public_preview")}
					</h4>
					<p className="text-xs text-gray-600 mb-3 italic">{t("public_preview_description")}</p>
					<div className="bg-white rounded-lg p-4 shadow-sm text-sm">
						<BearerMedicalDisplay
							t={t}
							isVerbal={isVerbal}
							diseases={diseases}
							birthYear={birthYear !== '-1' ? parseInt(birthYear) : -1}
							sex={sex}
							bloodType={bloodType}
						/>
					</div>
				</div>
				
				{/* MEDICAL INFORMATION SECTION */}
				<div className="mt-6 border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
					
					{!isInOverviewEditMode ? (
						<>
							{/* DISPLAY MODE - BUTTON ONLY */}
							<button
								onClick={() => setIsInOverviewEditMode(true)}
								disabled={isSaving || isEditingWristband || isEditingHealthConditions}
								className="w-full bg-purple-600 text-white font-semibold py-3 px-6 rounded-md text-sm hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
								{t("edit_medical_information")}
							</button>
						</>
					) : (
						<>
							{/* EDIT MODE */}
							<div className="bg-white rounded-lg p-4 mb-4 space-y-4">
								{/* IS VERBAL CHECKBOX */}
								<div className="flex items-center justify-between">
									<label htmlFor="bearer-detail-nonverbal" className="text-sm font-medium text-gray-700">
										{t("person_cannot_talk")}
									</label>
									<input
										disabled={isSaving}
										type="checkbox"
										id="bearer-detail-nonverbal"
										checked={!isVerbal}
										onChange={(e) => saveIsVerbal(!e.target.checked)}
										name="isVerbal"
										className="h-5 w-5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
									/>
								</div>
						
						
						{/* NAME */}
						<div>
							<label htmlFor="bearer-detail-name" className="block text-sm font-medium text-gray-700 mb-2">
								{t("name")}
							</label>
							<input 
								disabled={isSaving || isEditingWristband || isEditingHealthConditions}
								type="text"
								id="bearer-detail-name"
								value={name}
								name="name"
								onChange={(e) => setName(e.target.value)}
								className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:ring-1 focus:ring-cyan-600"
							/>
						</div>
						
						{/* BIRTH YEAR */}
						<div>
							<label htmlFor="bearer-detail-birthyear" className="block text-sm font-medium text-gray-700 mb-2">
								{t("birth_year")}
							</label>
							<select
								disabled={isSaving || isEditingWristband || isEditingHealthConditions}
								id="bearer-detail-birthyear"
								value={birthYear}
								onChange={(e) => {
									const newValue = e.target.value;
									setBirthYear(newValue);
									saveBirthYear(newValue);
								}}
								className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:ring-1 focus:ring-cyan-600"
							>
								<option value="-1">{t("sex_unspecified")}</option>
								{Array.from({length: new Date().getFullYear() - 1920 + 1}, (_, i) => new Date().getFullYear() - i).map(year => (
									<option key={year} value={year}>{year}</option>
								))}
							</select>
						</div>
					
						{/* SEX */}
						<div>
							<label htmlFor="bearer-detail-sex" className="block text-sm font-medium text-gray-700 mb-2">
								{t("sex")}
							</label>
							<select
								disabled={isSaving || isEditingWristband || isEditingHealthConditions}
								id="bearer-detail-sex"
								value={sex}
								onChange={(e) => {
									const newValue = e.target.value;
									setSex(newValue);
									saveSex(newValue);
								}}
								className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:ring-1 focus:ring-cyan-600"
							>
								<option value="unspecified">{t("sex_unspecified")}</option>
								<option value="female">{t("sex_female")}</option>
								<option value="male">{t("sex_male")}</option>
							</select>
						</div>
						
						{/* BLOOD TYPE */}
						<div>
							<label htmlFor="bearer-detail-bloodtype" className="block text-sm font-medium text-gray-700 mb-2">
								{t("blood_type")}
							</label>
							<select
								disabled={isSaving || isEditingWristband || isEditingHealthConditions}
								id="bearer-detail-bloodtype"
								value={bloodType}
								onChange={(e) => {
									const newValue = e.target.value;
									setBloodType(newValue);
									saveBloodType(newValue);
								}}
								className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:ring-1 focus:ring-cyan-600"
							>
								<option value="unspecified">{t("blood_type_unspecified")}</option>
								<option value="A+">A+</option>
								<option value="A-">A-</option>
								<option value="B+">B+</option>
								<option value="B-">B-</option>
								<option value="AB+">AB+</option>
								<option value="AB-">AB-</option>
								<option value="O+">O+</option>
								<option value="O-">O-</option>
							</select>
						</div>
					</div>
					<button
						onClick={() => {
							// Save name if it changed
							if (name !== bearerDetails.name) {
								saveName(name);
							}
							setIsInOverviewEditMode(false);
						}}
						disabled={isSaving}
						className="w-full bg-purple-600 text-white font-semibold py-3 px-6 rounded-md text-sm hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
						{t("done_editing")}
					</button>
				</>
			)}
		</div>

{/* HEALTH CONDITIONS SECTION */}
<div className="mt-6 border-2 border-green-200 rounded-lg p-4 bg-green-50">

	<MultiDiseaseSelector
		loginToken={loginToken}
		initialDiseases={diseases}
		onDiseasesChange={setDiseases}
		onEditingChange={setIsEditingHealthConditions}
		onSave={saveDiseases}
		disabled={isSaving || isEditingWristband || isInOverviewEditMode}
	/>
</div>

{/* AUDIT LOG SECTION (admin only) */}
{(userType === 'ADMIN' || userType === 'SUPERUSER') && (
	<BearerAuditLog loginToken={loginToken} bearerId={bearerId} />
)}

{/* ACCESS LOG SECTION (admin only) */}
{(userType === 'ADMIN' || userType === 'SUPERUSER') && (
	<BearerAccessLog loginToken={loginToken} bearerId={bearerId} />
)}

{/* ACTIONS SECTION */}
<div className="mt-6 border-2 border-red-200 rounded-lg p-4 bg-red-50">
	<button
		disabled={isSaving || isEditingWristband || isInOverviewEditMode || isEditingHealthConditions}
		onClick={() => setShowDeleteConfirm(true)}
		className="w-full bg-red-600 text-white font-semibold py-3 px-6 rounded-md text-sm hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
			Avregistrera person
	</button>
</div>
				{showDeleteConfirm && (
					<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
						<div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
							<h3 className="text-lg font-bold text-gray-900 mb-4">{t("confirm_delete_bearer")}</h3>						
						{/* Warning message */}
						<div className="bg-red-50 border-l-4 border-red-600 p-4 mb-4">
							<div className="flex">
								<div>
									<p className="text-sm font-semibold text-red-800">{t("delete_bearer_warning_title")}</p>
									<p className="text-sm text-red-700 mt-1">{t("delete_bearer_warning_message")}</p>
								</div>
							</div>
						</div>
						<p className="text-sm text-gray-700 mb-4">
							{t("type_bearer_name_to_confirm")}: <span className="font-bold">{bearerDetails.name}</span>
						</p>
						<input
							type="text"
							value={deleteConfirmName}
							onChange={(e) => setDeleteConfirmName(e.target.value)}
							placeholder={t("enter_bearer_name")}
							className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-cyan-600"
						/>
						<div className="flex gap-2 justify-end">
							<button
								onClick={() => {
									setShowDeleteConfirm(false);
									setDeleteConfirmName('');
								}}
								className="btn-main text-white px-4 py-2 rounded-md text-sm">
								{t("cancel")}
							</button>
							<button
								onClick={confirmDeleteBearer}
								disabled={deleteConfirmName !== bearerDetails.name}
								className="bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
								{t("delete_bearer")}
							</button>
						</div>
						</div>
					</div>
				)}
			</div>
			{/* We list scan tokens and their scans on a different page for now */}
			<ScanTokens
				loginToken={loginToken}
				bearerId={bearerId}			bearerName={bearerDetails?.name}				updateListOfScanTokens={updateListOfScanTokens}
				isEditingBearer={isInOverviewEditMode || isEditingHealthConditions}
				reportWristbandEditing={setIsEditingWristband} />
		</div>
	);
};

export default BearerDetails;
